export const PUBLIC_CV_MAX_BYTES = 10_000_000;
export const PUBLIC_ATTACHMENT_MAX_BYTES = 20_000_000;

function asBoolean(value, fallback = false) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }
  return fallback;
}

function firstConfiguredValue(sources, keys) {
  for (const source of sources) {
    for (const key of keys) {
      if (source?.[key] !== undefined && source?.[key] !== null) return source[key];
    }
  }
  return undefined;
}

function firstConfiguredArray(sources, keys) {
  for (const source of sources) {
    for (const key of keys) {
      if (Array.isArray(source?.[key]) && source[key].length) return source[key];
    }
  }
  return [];
}

export function normalizePublicQuestionType(value) {
  const normalized = String(value || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
  if (normalized.includes('video')) return 'video';
  if (['textarea', 'paragraph', 'longtext', 'essay'].includes(normalized)) return 'textarea';
  if (['url', 'link', 'website'].includes(normalized)) return 'url';
  if (['file', 'upload', 'document', 'attachment', 'fileupload'].includes(normalized)) return 'file';
  if (['select', 'dropdown'].includes(normalized)) return 'select';
  if (['yesno', 'boolean', 'bool', 'radio'].includes(normalized)) return 'radio';
  if (['checkbox', 'multiselect'].includes(normalized)) return 'checkbox';
  return 'text';
}

export function normalizePublicApplicationQuestions(items) {
  if (!Array.isArray(items)) return [];
  return items.map((item, index) => {
    if (typeof item === 'string') {
      const label = item.trim();
      return label ? {
        id: `custom_q_${index + 1}`,
        label,
        type: 'text',
        required: false,
        options: [],
        systemKey: '',
      } : null;
    }
    if (!item || typeof item !== 'object') return null;
    const label = String(
      item.label || item.question || item.text || item.title || item.name || item.fieldName || '',
    ).trim();
    if (!label) return null;
    const explicitType = item.type || item.inputType || item.fieldType || item.questionType
      || item.answerType || item.responseType || item.format || item.controlType;
    const identity = String(item.systemKey || item.id || '').toLowerCase().replace(/[\s_-]+/g, '');
    return {
      id: String(item.id || item.key || `custom_q_${index + 1}`),
      label,
      type: identity === 'videosubmission' ? 'video' : normalizePublicQuestionType(explicitType),
      required: asBoolean(item.required ?? item.isRequired ?? item.mandatory, false),
      options: (Array.isArray(item.options) ? item.options : [])
        .map((option) => String(
          option && typeof option === 'object' ? option.label || option.value || option.text || '' : option || '',
        ).trim())
        .filter(Boolean),
      systemKey: String(item.systemKey || '').trim(),
    };
  }).filter(Boolean);
}

export function resolvePublicApplicationConfig(job = {}) {
  const candidates = [
    job.applicationFormConfig,
    job.publicJobApplicationSettings,
    job.publicJobApplication,
    job.publicJobPageSettings,
    job.applicationSettings,
    job.applicationForm,
    job.formConfig,
    job.settings?.publicJobApplication,
    job.settings?.publicJobApplicationSettings,
    job,
  ].filter(Boolean);
  const fields = firstConfiguredArray(candidates, ['fields', 'applicationFields', 'formFields']);
  const isCoverLetterField = (field) => String(field?.systemKey || '').trim() === 'coverLetter';
  const coverLetterField = fields.find(isCoverLetterField);
  const coverLetter = candidates[0]?.coverLetter || {};
  const coverLetterRequired = asBoolean(
    firstConfiguredValue(candidates, [
      'coverLetterRequired', 'requireCoverLetter', 'requireCoverLetterUpload', 'isCoverLetterRequired',
    ]) ?? coverLetter.required ?? coverLetterField?.required,
    false,
  );
  const coverLetterEnabled = asBoolean(
    firstConfiguredValue(candidates, [
      'coverLetterEnabled', 'allowCoverLetter', 'enableCoverLetter', 'coverLetterOptional',
      'allowCoverLetterUpload',
    ]) ?? coverLetter.enabled ?? Boolean(coverLetterField),
    false,
  ) || coverLetterRequired;
  const questionSource = fields.length
    ? fields.filter((field) => !isCoverLetterField(field))
    : firstConfiguredArray(candidates, [
      'customQuestions', 'applicationQuestions', 'requiredFields', 'screeningQuestions',
      'additionalQuestions', 'questions',
    ]);
  const questions = normalizePublicApplicationQuestions(questionSource);
  const orderedFields = fields.length
    ? fields.map((field) => {
      if (isCoverLetterField(field)) {
        return {
          id: String(field.id || 'cover_letter_upload'),
          label: String(field.label || 'Cover letter'),
          type: 'file',
          required: asBoolean(field.required ?? field.isRequired, false),
          options: [],
          systemKey: 'coverLetter',
        };
      }
      return normalizePublicApplicationQuestions([field])[0] || null;
    }).filter(Boolean)
    : [...questions];

  if (coverLetterEnabled && !orderedFields.some(isCoverLetterField)) {
    orderedFields.unshift({
      id: 'cover_letter_upload',
      label: 'Cover letter',
      type: 'file',
      required: coverLetterRequired,
      options: [],
      systemKey: 'coverLetter',
    });
  }

  return {
    coverLetterEnabled,
    coverLetterRequired,
    questions,
    orderedFields,
  };
}

export function safePublicJobId(value) {
  const cleaned = String(value || '').trim();
  return /^[A-Za-z0-9_-]{1,180}$/.test(cleaned) ? cleaned : '';
}

export function safePublicStorageSegment(value, fallback = 'file') {
  const cleaned = String(value || '').trim().replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '');
  return cleaned.slice(0, 100) || fallback;
}

export function validLinkedInUrl(value) {
  try {
    const parsed = new URL(String(value || '').trim());
    return parsed.protocol === 'https:' && /(^|\.)linkedin\.com$/i.test(parsed.hostname);
  } catch (_error) {
    return false;
  }
}

export function profileApplicationFields(profile = {}) {
  const personal = profile.personalInfo && typeof profile.personalInfo === 'object' ? profile.personalInfo : {};
  const username = String(profile.username || profile.fullName || personal.fullName || '').trim();
  const nameParts = username.split(/\s+/).filter(Boolean);
  return {
    firstName: String(profile.firstName || personal.firstName || personal.first_name || nameParts[0] || '').trim(),
    lastName: String(
      profile.lastName || personal.lastName || personal.last_name || nameParts.slice(1).join(' '),
    ).trim(),
    linkedInProfile: String(
      profile.linkedInProfile || profile.linkedinUrl || profile.linkedin || personal.linkedInProfile
      || personal.linkedinUrl || '',
    ).trim(),
  };
}

export function answerHasValue(question, value) {
  if (question.type === 'checkbox') return Array.isArray(value) && value.length > 0;
  if (question.type === 'file' || question.type === 'video') return Boolean(value?.name || value?.fileName);
  return Boolean(String(value ?? '').trim());
}
